import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import { BACKEND_API_URL, BRANCH_NAMES_API_URL } from "../../config";

// Slice
const initialState = {
  data: {
      _type: 'Standalone',
      _branch: {
                "BranchNickname": "Innovation Mortgage",
                "CostCenterID": "S10300B103R00D10",
                "Address": "7791 NW 46 Street, Suite 417",
                "BranchManager": "",
                "State": "FL",
                "City": "Doral",
                "ZipCode": "33166"
            },
      _option: 'Existing Location with Lease',
      address: '',
      tenant: '',
      landlord: '',
      landlordPhoneNumber: '',
      landlordEmail: '',
      amount: '',  // Monthly Amount + taxes,
      agreementFile: null,
      utilities: {
        _option: '',
        data: [], // store object { 'name': 'utility selected', data: {formData}}
        otherUtilities: '',
        additionalServices: '',
      },
    },
  branchNames: [],
  status: 'idle', // 'idle' | 'loading' | 'success' | 'failed'
  loading: false,
  error: null,
  formErrors: {},
}

export const fetchInitialState = createAsyncThunk(
  'BranchLocationDetails/fetch', 
  async (data, { getState, dispatch, rejectWithValue }) => {
      const token = getState().navigator.token;
      try {

        const response = await fetch(`${BACKEND_API_URL}/api/onboarding/branch_location?token=${token}`);
        if (!response.ok) {
          return rejectWithValue('Failed to fetch initial state');
        }
        const initialState = await response.json();
        // // console.log(initialState);
        return initialState;
        
      } catch (error) {
        console.error('Error fetching initial state:', error);
        return rejectWithValue(error.message)
      }
    
  })
export const fetchBranchNames = createAsyncThunk(
    'BranchLocationDetails/fetchBranchNames', 
    async (data, { getState, dispatch, rejectWithValue }) => {
     
        try {
          const response = await fetch(`${BRANCH_NAMES_API_URL}`, {
                            method: 'POST',
                            headers: {
                              'Cookie': 'ARRAffinity=0aa69915266871205a67096b40953eafb333722c9d662666b4ee1cbd3af96c28; ARRAffinitySameSite=0aa69915266871205a67096b40953eafb333722c9d662666b4ee1cbd3af96c28'
                            }
                      })
          if (!response.ok) {
            return rejectWithValue('Failed to fetch branch names');
          }
          const response_data = await response.json();
          let branch_names = response_data?.ResultSets?.Table1 || [];
          let branch_names_sorted = branch_names?.sort((a, b) => a.BranchNickname.localeCompare(b.BranchNickname))
          return branch_names_sorted;
          
        } catch (error) {
          console.error('Error fetching initial state:', error);
          return rejectWithValue(error.message)
        }
      
    })

// Only the fields rendered on the Branch Location Details form (step 8).
// The stored branch_location object also carries admin-seeded keys (division,
// region, branch, city, state, costCenterLocation, filledBy, ...) that are blank
// and have no input on this form, so they must never be part of this check.
const LOCATION_DETAILS_REQUIRED_FIELDS = [
  'address',
  'tenant',
  'landlord',
  'landlordPhoneNumber',
  'landlordEmail',
  'amount',
];

export const SubmitBranchLocationDetails = createAsyncThunk(
  'BranchLocationDetails/submit',
  async (data, { getState, rejectWithValue }) => {
      try {
        const token = getState().navigator.token;
        const { currentStep } = getState().navigator;

        const { formErrors } = getState().branch_location;

        // A file upload / delete is a partial save: only the file changed, so the
        // rest of the form is allowed to still be empty at that point.
        const isFileOnlySave = data.agreementFile instanceof File || data.agreementFile === 'delete';

        // formErrors is shared with the Utilities step, which stores its messages
        // nested under the utility card name (formErrors.Other.otherUtilities).
        // Only this form's own flat field errors may block this submit — otherwise
        // a stale utilities error rejects a fully valid Branch Location Details
        // form, with no field on this page marked to explain why.
        const isFormValid = LOCATION_DETAILS_REQUIRED_FIELDS.every(
          (field) => !formErrors[field]
        );

        if (!isFormValid) {
          return rejectWithValue("Please enter valid data in all the fields");
        }

        // Ensure file is uploaded for Branch Location Details step
        if (currentStep === 8 && !isFileOnlySave) {
          const isFormFilled = LOCATION_DETAILS_REQUIRED_FIELDS.every(
            (field) => data[field] !== undefined && data[field] !== null && String(data[field]).trim() !== ''
          );
          if (!isFormFilled) {
            return rejectWithValue('Please fill out all the fields');
          }
          if (!data.agreementFile || data.agreementFile === 'null') {
            return rejectWithValue('Please upload the agreement file');
          }
        }

        // Prepare FormData (excluding utilities)
        const formData = new FormData();
        Object.keys(data).forEach((key) => {
          if (typeof data[key] === 'object' && !(data[key] instanceof File)) {
            // Convert non-File objects to JSON strings
            formData.append(key, JSON.stringify(data[key]));
          } else {
            formData.append(key, data[key]);
          }
        });
  
        // Add currentStep to FormData. The backend advances the saved step by one
        // whenever current_step is present, so a file-only save must omit it —
        // otherwise uploading the agreement would move the user past this step.
        if (!isFileOnlySave) {
          formData.append('current_step', currentStep);
        }
  
        // Submit the form
        const response = await fetch(
          `${BACKEND_API_URL}/api/onboarding/branch_location?token=${token}`,
          {
            method: 'POST',
            body: formData,
          }
        );
  
        if (!response.ok) {
          throw new Error("Failed to submit branch location details");
        }
  
        const response_data = await response.json();
        return response_data.data;
      } catch (error) {
        console.error('Form submission error', error);
        return rejectWithValue(error.message);
      }
  }
);


export const SubmitUtilities = createAsyncThunk(
  'BranchLocationDetails/submitUtilities',
  async (utilitiesData, { getState, rejectWithValue }) => {
    try {
      
      const token = getState().navigator.token;
    
      // Validate utilities
      const utilities = utilitiesData;
      if ( utilities._option === '') {
          return rejectWithValue("Please select the option.");
      }
      
      if (utilities._option === 'Yes' && utilities.data.length === 0) {
          return rejectWithValue("Please select the applicable utilities!");
      }

      // The bill / statement is optional and is saved on its own as soon as it is
      // picked or dropped, so an upload / delete is a partial save: the other
      // utilities are allowed to still be incomplete at that point.
      const isFileOnlySave = utilities.data.some(
        (utility) =>
          utility.data?.attachment instanceof File || utility.data?.attachment === 'delete'
      );

      let message = "Company name is required for the selected utilities.";
      const isUtilitiesValid = utilities.data.every((utility) => {
        // The "Other" card is a single free text field — it carries no company name
        if (utility.name === 'Other') {
          // The API drops blank keys, so a card the user never filled in comes
          // back without otherUtilities at all rather than as an empty string.
          // That, and whitespace only, all count as missing.
          if (String(utility.data.otherUtilities ?? '').trim() === '') {
            message = "Please enter other utilities. Otherwise, do not select the 'Other' option.";
            return false;
          }
          return true;
        }
        // Same as otherUtilities above: the API drops blank keys, so a card saved
        // earlier by a file-only upload comes back without companyName at all —
        // that is still a missing company name, not a filled one.
        if (String(utility.data.companyName ?? '').trim() === '') {
          message = `Please enter the name of the company for ${utility.name}.`;
          return false;
        }
        return true;
      });

      if (!isUtilitiesValid && !isFileOnlySave) {
        return rejectWithValue(message);
      }


      // Create a new FormData instance
      const formData = new FormData();
      function buildFormData(formData, data, parentKey = '') {
          if (data instanceof File) {
              // Append the file directly
              formData.append(parentKey, data);
          } else if (Array.isArray(data)) {
              // Iterate over array elements
              data.forEach((value, index) => {
                  const arrayKey = `${parentKey}[${index}]`;
                  buildFormData(formData, value, arrayKey);
              });
          } else if (data && typeof data === 'object') {
              // Iterate over object properties
              Object.keys(data).forEach((key) => {
                  const newKey = parentKey ? `${parentKey}[${key}]` : key;
                  buildFormData(formData, data[key], newKey);
              });
          } else {
              // Append primitive values
              formData.append(parentKey, data == null ? '' : data);
          }
      }

      buildFormData(formData, utilitiesData);



      // Send FormData to the backend
      const response = await fetch(`${BACKEND_API_URL}/api/onboarding/utilities?token=${token}`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to submit utilities");
      }

      const responseData = await response.json();
      return responseData.data;
    } catch (error) {
      console.error("Utilities submission error:", error);
      return rejectWithValue(error.message);
    }
  }
)


export const branchLocationDetailsSlice = createSlice({
    name: 'BranchLocationDetails',
    initialState,
    reducers: {
      updateBranchLocationType: (state, action) => {
        if (action.payload === 'Satellite' && state.data._branch?.BranchNickname === "") {
          state.data._branch =  {
                "BranchNickname": "Innovation Mortgage",
                "CostCenterID": "S10300B103R00D10",
                "Address": "7791 NW 46 Street, Suite 417",
                "BranchManager": "",
                "State": "FL",
                "City": "Doral",
                "ZipCode": "33166"
            }
        }
        state.data._type = action.payload;
      },
      updateBranchLocationOption: (state, action) => {
        state.data._option = action.payload;
      },
      updateBranchLocationAddress: (state, action) => {
        const { field, value } = action.payload;
        if (field === '_branch') {
          let newValue = state.branchNames.filter(branch => branch.BranchNickname === value)[0]
          return { ...state, data: {...state.data, [field]: newValue }}
        }
        else {
          return { ...state, data: {...state.data, [field]: value }}
        }
        
      },
      updateUtilities: (state, action) => {
        // state.data.utilities.data = state.data.utilities.data.includes(action.payload)
        // ? state.data.utilities.data.filter(item => item !== action.payload)
        // : [...state.data.utilities.data, action.payload]
        // // console.log(action.payload);
        const utilityName = action.payload;
        const index = state.data.utilities.data.findIndex(utility => utility.name === utilityName);
        // // console.log(index);
        if (index !== -1) {
          // Utility already exists, remove it (toggle off)
          state.data.utilities.data.splice(index, 1);
        } else {
          if (utilityName === 'Other') {
            state.data.utilities.data.push({
              name: utilityName,
              data: { 'otherUtilities': '' }
            });
          }
          else {
            // Utility does not exist, add it (toggle on) with initial form data
          state.data.utilities.data.push({
            name: utilityName,
            data: {
              // Initialize with empty form data or default values
              companyName: '',
              contactEmail: '',
              contactNumber: '',
              accountNumber: '',
              serviceName: '',
              notes: '',
              attachment: null,
            }
          });
          }
        }
        state.formErrors[utilityName] = {}
      },
      setUtilityData: (state, action) => {
        const { utilityName, field, value } = action.payload;
        const utility = state.data.utilities.data.find(u => u.name === utilityName);
        if (utility) {          
          if (field === 'attachment') {
              // Store only the file name or metadata              
              utility.data[field] = value?.name || null;
          } else {
              utility.data[field] = value; // Update other fields as usual
          }
        }
      },
      updateUtilitiesOption: (state, action) => {
        state.data.utilities = {
          ...state.data.utilities, // Spread the existing utilities state
          _option: action.payload,
          ...(action.payload === 'No' && { // Reset fields if "No" is selected
            data: [],
            otherUtilities: '',
            additionalServices: '',
          }),
        };
      },
      updateUtilitiesOther: (state, action) => {
        const { field, value } = action.payload;
        return { ...state, data: { ...state.data, utilities: { ...state.data.utilities, [field]: value } } }
      },
      setFieldError: (state, action) => {
        const { utilityName, field, error } = action.payload;
        // console.log("utilityName, field, error: ", utilityName, field, error);
        // // console.log(field);
        // // console.log(error);
        if (utilityName) {
          if (state.formErrors[utilityName]) {
            state.formErrors[utilityName][field] = error;
          }
          else {
            state.formErrors[utilityName] = { [field]: error };
          }
        }
        else {
          state.formErrors[field] = error;
        }
      },
    },
    extraReducers: (builder) => {
        builder
          // FETCH
          .addCase(fetchInitialState.pending, (state) => {
            state.loading = true;
          })
          .addCase(fetchInitialState.fulfilled, (state, action) => {
            state.loading = false;
            state.data = action.payload;
          })
          .addCase(fetchInitialState.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
          })

          // CREATE or UPDATE
          .addCase(SubmitBranchLocationDetails.pending, (state) => {
            state.loading = true;
          })
          .addCase(SubmitBranchLocationDetails.fulfilled, (state, action) => {
            state.loading = false;
            state.data = action.payload;
          })
          .addCase(SubmitBranchLocationDetails.rejected, (state, action) => {
            // // console.log(action.payload);
            state.loading = false;
            state.error = action.payload;
          })

           // FETCH Branch Names
           .addCase(fetchBranchNames.pending, (state) => {
            state.loading = true;
          })
          .addCase(fetchBranchNames.fulfilled, (state, action) => {
            state.loading = false;
            state.branchNames = action.payload;
          })
          .addCase(fetchBranchNames.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
          })

          .addCase(SubmitUtilities.pending, (state) => {
            state.loading = true;
          })
          .addCase(SubmitUtilities.fulfilled, (state, action) => {
            state.loading = false;
            state.data.utilities = action.payload;
          })
          .addCase(SubmitUtilities.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
          });
      },
  });


export const { updateBranchLocationType, 
                updateBranchLocationOption, 
                updateBranchLocationAddress, 
                updateUtilities,
                setUtilityData,
                updateUtilitiesOption,
                updateUtilitiesOther, 
                setFieldError } = branchLocationDetailsSlice.actions;

export default branchLocationDetailsSlice.reducer;
  